/**
 * @pit/math — imath.ts
 *
 * The ONE file inside @pit/math allowed to use raw `/`, `%`, `Math.floor`,
 * `Math.sqrt` — because it *implements* idiv/imod/isqrt on top of them.
 *
 * See wp0-scaffold-ci.md § "Layer 1: ESLint bans" escape-hatch rows.
 * See wp1-protocol.md Part A for the semantics.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * Overflow policy (decided once, here — do NOT "helpfully" add BigInt):
 *
 * All sim quantities fit in ±2^31. Arena ≤ 4096 units per axis, speeds ≤
 * ~200 units/tick, so `dx * speed` ≤ ~8e5 and `distSq` ≤ ~3.4e7 — trivially
 * safe inside the float53 safe-integer range. `assertInt` guards
 * MAX_SAFE_INTEGER anyway. `imul`-style wrap-around is used ONLY inside
 * `packages/sim/src/hash.ts` (FNV-1a), never elsewhere.
 * ─────────────────────────────────────────────────────────────────────────
 */

/**
 * Typed error for math contract violations (non-integer input, division by
 * zero, negative sqrt). Deliberately NOT a subclass of any provider-specific
 * base class — it is standalone so consumers can catch it structurally.
 */
export class SimMathError extends Error {
  public override readonly name = "SimMathError";
  public readonly label: string;
  public readonly value: unknown;

  public constructor(label: string, value: unknown, detail: string) {
    super(`SimMathError(${label}): ${detail} (received: ${describe(value)})`);
    this.label = label;
    this.value = value;
  }
}

function describe(value: unknown): string {
  if (typeof value !== "number") return String(value);
  if (Number.isNaN(value)) return "NaN";
  if (value === Infinity) return "Infinity";
  if (value === -Infinity) return "-Infinity";
  return String(value);
}

/**
 * Assert that `x` is a finite integer within safe-integer range. Throws
 * SimMathError on NaN, ±Infinity, non-integer floats, or |x| >
 * MAX_SAFE_INTEGER. Also normalizes `-0` conceptually: `-0` is a valid
 * integer here (Number.isInteger(-0) === true); callers that care about
 * `-0` vs `+0` (schemas, hashers) must normalize themselves.
 */
export function assertInt(x: number, label: string): asserts x is number {
  if (typeof x !== "number") {
    throw new SimMathError(label, x, "expected number");
  }
  if (Number.isNaN(x)) {
    throw new SimMathError(label, x, "NaN is not a valid integer");
  }
  if (!Number.isFinite(x)) {
    throw new SimMathError(label, x, "Infinity is not a valid integer");
  }
  if (!Number.isInteger(x)) {
    throw new SimMathError(label, x, "value is not an integer");
  }
  if (x > Number.MAX_SAFE_INTEGER || x < -Number.MAX_SAFE_INTEGER) {
    throw new SimMathError(label, x, "|value| exceeds MAX_SAFE_INTEGER");
  }
}

/**
 * Floored integer division. `idiv(-7, 2) === -4`.
 * Throws SimMathError on non-integer args or b === 0.
 */
export function idiv(a: number, b: number): number {
  assertInt(a, "idiv.a");
  assertInt(b, "idiv.b");
  if (b === 0) {
    throw new SimMathError("idiv.b", b, "division by zero");
  }
  const q = Math.floor(a / b);
  // Normalize -0 → 0. `Math.floor(0 / -5) === -0`; -0 diverges from 0 in
  // Object.is and in some hash paths (see wp1-protocol.md "Traps").
  return q === 0 ? 0 : q;
}

/**
 * Floored modulo: the result's sign follows the divisor.
 * `imod(-7, 2) === 1`, `imod(7, -2) === -1`.
 * Identity: `a === idiv(a,b)*b + imod(a,b)` for all `b !== 0`.
 * Throws SimMathError on non-integer args or b === 0.
 */
export function imod(a: number, b: number): number {
  assertInt(a, "imod.a");
  assertInt(b, "imod.b");
  if (b === 0) {
    throw new SimMathError("imod.b", b, "modulo by zero");
  }
  // NB: `a - Math.floor(a / b) * b` (not `((a % b) + b) % b`) — direct
  // floored-modulo formulation; matches BigInt property tests.
  const r = a - Math.floor(a / b) * b;
  // Normalize -0 → 0 (see idiv above).
  return r === 0 ? 0 : r;
}

/**
 * Floored integer square root. `isqrt(10) === 3` (10 < 16, ≥ 9).
 * Throws SimMathError on non-integer or negative input.
 *
 * Implementation: `Math.floor(Math.sqrt(n))` seeds the answer, then TWO
 * correction loops fix off-by-one drift from float ulp error near large
 * perfect squares. Both loops are MANDATORY:
 *   while (r*r > n) r--;
 *   while ((r+1)*(r+1) <= n) r++;
 *
 * Property test in index.test.ts verifies `r*r ≤ n < (r+1)*(r+1)` over the
 * full 0..2^50 range where float sqrt can drift.
 */
export function isqrt(n: number): number {
  assertInt(n, "isqrt.n");
  if (n < 0) {
    throw new SimMathError("isqrt.n", n, "isqrt of negative value");
  }
  if (n < 2) return n;
  let r = Math.floor(Math.sqrt(n));
  // Overshoot correction: float sqrt may return exactly the ceil for some n.
  while (r * r > n) r--;
  // Undershoot correction: float sqrt may round down one ulp too far.
  while ((r + 1) * (r + 1) <= n) r++;
  return r;
}
